/**
 * ConnectorService
 *
 * Management surface for the LLM (MCP) connector, served by `CCMS.Api.UserManagement`
 * (gateway prefix `users`). Not the MCP endpoint itself — that lives at mcp.actschurch.co.za
 * and is spoken to by LLM clients, never by this SDK.
 *
 * Routes — /api/connector
 *  USER (any signed-in user)
 *   - GET  /my-grants                          — the caller's connected LLM clients
 *   - POST /grants/{grantId}/revoke            — disconnect one (own; admins: anyone's)
 *  ADMIN (Connector.Admin right)
 *   - GET  /grants?liveOnly=&take=             — every grant on the system
 *   - GET  /logs?userId=&toolName=&failuresOnly=&skip=&take=   — audit trail, newest first
 *   - GET  /clients                            — registered OAuth clients
 *   - POST /clients/{clientId}/deactivate      — deactivate + revoke all its grants
 *   - GET  /settings                           — kill switches
 *   - PUT  /settings/{name}  { value }         — flip one
 *
 * A revoke takes effect on the session's next call, not at token expiry: the server mirrors
 * it into a Redis set the MCP server checks on every request.
 */
import { BaseService } from '../base/BaseService';
export class ConnectorService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ConnectorService.DEFAULT_SERVICE_PATH,
            basePath: ConnectorService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== USER ====================
    /** The caller's own connected LLM clients, revoked ones included. GET /my-grants */
    async getMyGrants() {
        const response = await this.getMyGrantsRaw();
        return response.data ?? [];
    }
    getMyGrantsRaw() {
        return this.http.get(this.buildUrl(this.getEndpoint('my-grants', 'my-grants')));
    }
    /** Disconnects a grant. Own grants for anyone; others' only with Connector.Admin. POST /grants/{id}/revoke */
    async revokeGrant(grantId) {
        const response = await this.revokeGrantRaw(grantId);
        return response.data ?? false;
    }
    revokeGrantRaw(grantId) {
        return this.http.post(this.buildUrl(this.getEndpoint('revoke', `grants/${grantId}/revoke`)), {});
    }
    // ==================== ADMIN ====================
    /** Every grant on the system, newest first. GET /grants */
    async getAllGrants(liveOnly = false, take = 200) {
        const response = await this.getAllGrantsRaw(liveOnly, take);
        return response.data ?? [];
    }
    getAllGrantsRaw(liveOnly = false, take = 200) {
        const url = this.buildUrlWithParams(this.getEndpoint('grants', 'grants'), { liveOnly, take });
        return this.http.get(url);
    }
    /** The audit trail, newest first. Returns the full envelope so callers can page on `hasMore`. GET /logs */
    getLogsRaw(filter = {}) {
        const url = this.buildUrlWithParams(this.getEndpoint('logs', 'logs'), {
            userId: filter.userId,
            toolName: filter.toolName,
            failuresOnly: filter.failuresOnly,
            skip: filter.skip ?? 0,
            take: filter.take ?? 50,
        });
        return this.http.get(url);
    }
    async getLogs(filter = {}) {
        const response = await this.getLogsRaw(filter);
        return response.data ?? [];
    }
    /** Registered OAuth clients. GET /clients */
    async getClients() {
        const response = await this.getClientsRaw();
        return response.data ?? [];
    }
    getClientsRaw() {
        return this.http.get(this.buildUrl(this.getEndpoint('clients', 'clients')));
    }
    /** Deactivates a client and revokes every grant issued to it. POST /clients/{clientId}/deactivate */
    async deactivateClient(clientId) {
        const response = await this.deactivateClientRaw(clientId);
        return response.data ?? false;
    }
    deactivateClientRaw(clientId) {
        return this.http.post(this.buildUrl(this.getEndpoint('deactivate-client', `clients/${encodeURIComponent(clientId)}/deactivate`)), {});
    }
    /** The kill switches. GET /settings */
    async getSettings() {
        const response = await this.getSettingsRaw();
        return response.data ?? [];
    }
    getSettingsRaw() {
        return this.http.get(this.buildUrl(this.getEndpoint('settings', 'settings')));
    }
    /** Flips one switch. Only names seeded by migration 245 exist; unknown names 404. PUT /settings/{name} */
    async setSetting(name, value) {
        const response = await this.setSettingRaw(name, value);
        return response.data ?? null;
    }
    setSettingRaw(name, value) {
        return this.http.put(this.buildUrl(this.getEndpoint('set-setting', `settings/${encodeURIComponent(name)}`)), { value });
    }
    // ── People (who may connect) ────────────────────────────────────────────────
    /** Everyone in the ConnectorUser role, with their sessions. Connector.Admin. */
    async getPeople() {
        return (await this.http.get(this.buildUrl(this.getEndpoint('people', 'people')))).data ?? [];
    }
    /** Is the ConnectorUser gate wired (role, right, link)? `problem` says what is missing. */
    async getPeopleStatus() {
        const r = await this.http.get(this.buildUrl(this.getEndpoint('people-status', 'people/status')));
        return r.data ?? { roleExists: false, rightExists: false, roleGrantsRight: false, members: 0, problem: r.message ?? 'Status unavailable' };
    }
    /** Give a person the ConnectorUser role (effective at their next sign-in). */
    async addPerson(userId) {
        const r = await this.http.post(this.buildUrl(this.getEndpoint('people', 'people')), { userId });
        if (!r.data)
            throw new Error(r.message ?? 'Could not add the person');
        return r.data;
    }
    /** Remove the ConnectorUser role; live sessions stop at their next call. */
    async removePerson(userId) {
        return (await this.http.post(this.buildUrl(this.getEndpoint('people-remove', `people/${encodeURIComponent(userId)}/remove`)), {})).data ?? false;
    }
}
ConnectorService.DEFAULT_SERVICE_PATH = 'users';
ConnectorService.BASE_PATH = 'api/connector';
//# sourceMappingURL=ConnectorService.js.map